/**
 * FO-307-A2 (batch-33) - consignatie/piket ruilverzoek.
 *
 * Gedeelde DTO's + Zod-request-schemas (één bron voor API-validatie én client-
 * side form-validatie). De portal-DTO's bevatten BEWUST geen tarieven (dat is
 * client-kostdata, niet voor de monteur-portal) - alleen week/dienst/klantnaam.
 */
import { z } from 'zod'

export const CONSIGNATIE_RUIL_STATUSSES = [
  'AANGEVRAAGD',
  'COLLEGA_GEACCEPTEERD',
  'COLLEGA_AFGEWEZEN',
  'GOEDGEKEURD',
  'AFGEWEZEN',
  'INGETROKKEN',
] as const
export type ConsignatieRuilStatus = (typeof CONSIGNATIE_RUIL_STATUSSES)[number]

export const CONSIGNATIE_RUIL_STATUS_LABELS: Record<ConsignatieRuilStatus, string> = {
  AANGEVRAAGD: 'Aangevraagd',
  COLLEGA_GEACCEPTEERD: 'Collega akkoord',
  COLLEGA_AFGEWEZEN: 'Collega bezwaar',
  GOEDGEKEURD: 'Goedgekeurd',
  AFGEWEZEN: 'Afgewezen',
  INGETROKKEN: 'Ingetrokken',
}

/** Een eigen consignatie-dienst zoals de monteur die in de portal ziet. */
export interface MijnConsignatieDienstDTO {
  roosterId: string
  weekNr: string
  startDate: string
  endDate: string
  status: string
  clientNaam: string | null
  /** Ligt de dienst in het verleden? (historie vs. komend) */
  isVerleden: boolean
  /** Is er al een openstaand ruilverzoek voor deze dienst? */
  heeftOpenRuil: boolean
}

/** Collega uit dezelfde consignatie-groep (zelfde klant) die je kunt voorstellen. */
export interface ConsignatieGroepCollegaDTO {
  employeeId: string
  naam: string
}

/** Rol van de ingelogde gebruiker in een ruilverzoek (voor de portal-weergave). */
export type ConsignatieRuilRol = 'aanvrager' | 'collega' | null

export interface ConsignatieRuilVerzoekDTO {
  id: string
  roosterId: string
  weekNr: string
  clientNaam: string | null
  aanvragerNaam: string
  collegaNaam: string
  reden: string
  status: ConsignatieRuilStatus
  collegaReactieReden: string | null
  beslissingReden: string | null
  createdAt: string
  /** Alleen gezet in de portal-response: de rol van de ingelogde monteur. */
  rol?: ConsignatieRuilRol
}

// ── Request-schemas ──────────────────────────────────────────────────────────

export const CreateConsignatieRuilSchema = z.object({
  roosterId: z.string().min(1, 'Kies een dienst.'),
  voorgesteldeCollegaId: z.string().min(1, 'Kies een collega.'),
  reden: z.string().trim().min(5, 'Geef een korte reden op (minimaal 5 tekens).'),
})
export type CreateConsignatieRuilInput = z.infer<typeof CreateConsignatieRuilSchema>

export const ConsignatieRuilCollegaReactieSchema = z.object({
  akkoord: z.boolean(),
  reden: z.string().trim().max(500).optional(),
})
export type ConsignatieRuilCollegaReactieInput = z.infer<typeof ConsignatieRuilCollegaReactieSchema>

export const ConsignatieRuilBeslisSchema = z
  .object({
    akkoord: z.boolean(),
    reden: z.string().trim().max(500).optional(),
  })
  // Bij een afwijzing is een reden verplicht (>= 5 tekens) - de monteur moet
  // begrijpen waarom de planner de ruil niet toestond.
  .refine((v) => v.akkoord || (v.reden !== undefined && v.reden.length >= 5), {
    path: ['reden'],
    message: 'Geef een reden op voor de afwijzing (minimaal 5 tekens).',
  })
export type ConsignatieRuilBeslisInput = z.infer<typeof ConsignatieRuilBeslisSchema>
